import { useQuery } from "@tanstack/react-query";
import type { AccountApi } from "./account";
import type { SignInMethodsApi } from "./signInMethods";
import { adapter } from "./adapter";
import { api, ApiError, unwrap } from "./client";
import type { components } from "./schema";

// Everything the web app knows about sign-in lives here. The backend runs the OIDC flow with Zitadel
// (backend-for-frontend): the browser goes to /api/auth/login, comes back with an HttpOnly session cookie,
// and never sees a token.

export type Provider = components["schemas"]["ProviderInfo"];
export type Me = components["schemas"]["Me"];

const DEFAULT_RETURN_TO = "/";

/**
 * Keeps `returnTo` only when it is a path on this origin, like the backend does (`auth::return_to`):
 * no scheme, no `//host`, no backslash tricks. Anything else falls back to the home page.
 */
export function safeReturnTo(returnTo: string | null | undefined): string {
  if (!returnTo?.startsWith("/")) return DEFAULT_RETURN_TO;
  if (returnTo.startsWith("//") || returnTo.includes("\\")) return DEFAULT_RETURN_TO;
  if (hasControlCharacter(returnTo)) return DEFAULT_RETURN_TO;
  return returnTo;
}

function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) if (value.charCodeAt(i) < 0x20) return true;
  return false;
}

/** Where a "Continue with …" button goes. A full-page navigation: the backend redirects to the provider. */
export function loginUrl(providerId: string, returnTo?: string | null): string {
  const query = new URLSearchParams({ provider: providerId, return_to: safeReturnTo(returnTo) });
  return `/api/auth/login?${query.toString()}`;
}

export interface SessionApi {
  providers(): Promise<Provider[]>;
  /** The signed-in player, or null when there is no valid session. */
  me(): Promise<Me | null>;
  logout(): Promise<void>;
}

/** The whole "auth" area, as its mock implements it: the session (here, in the first load), the account and
 * onboarding routes (`./account`) and the sign-in methods (`./signInMethods`), loaded with the screens that use
 * them. */
export interface AuthApi extends SessionApi, AccountApi, SignInMethodsApi {}

export const authApi: SessionApi = adapter("auth", {
  async providers() {
    return unwrap(await api.GET("/api/auth/providers")).providers;
  },
  async me() {
    try {
      return unwrap(await api.GET("/api/me"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  async logout() {
    unwrap(await api.POST("/api/auth/logout"));
  },
});

export const fetchProviders = () => authApi.providers();
export const fetchMe = () => authApi.me();
export const logout = () => authApi.logout();

/** Why a sign-in failed. The backend's callback redirects to `/?auth_error=<code>` (`auth::ERROR_REDIRECT`). */
export const AUTH_ERRORS = ["expired", "denied", "banned", "failed"] as const;
export type AuthErrorCode = (typeof AUTH_ERRORS)[number];

export function authError(params: URLSearchParams): AuthErrorCode | null {
  const code = params.get("auth_error");
  if (code === null) return null;
  return (AUTH_ERRORS as readonly string[]).includes(code) ? (code as AuthErrorCode) : "failed";
}

export const authKeys = {
  me: ["auth", "me"] as const,
  providers: ["auth", "providers"] as const,
};

export function useMe() {
  // The session changes only through sign-in, sign-out and account actions, which set this cache themselves.
  return useQuery({ queryKey: authKeys.me, queryFn: fetchMe, staleTime: Infinity });
}

export function useProviders() {
  return useQuery({ queryKey: authKeys.providers, queryFn: fetchProviders, staleTime: Infinity });
}
